import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Button, Card, CardContent, CardHeader, Grid, Typography } from '@mui/material';

import { roleGridColumns } from './role-grid-columns';
import { UserRoleDialog } from '../user-role/user-role.dialog';
import { UserPermissionName } from '../../../models/user-permission-name';
import { SiTrackGrid } from '../../../components/si-track-grid/si-track-grid';
import { gridApiKeyStore } from '../../../api/grid-api-key-store';
import { ApiResponse } from '../../../models/api-response';
import { useLoadData } from '../../../hooks/useLoadData';
import { GridColumnConfig, GridPreference } from '../../../models/gridCoumnConfig';
import { useSaveData } from '../../../hooks/useSaveData';
import useDebounce from '../../../hooks/use-debounce';
import { State } from '@progress/kendo-data-query';

const styling = {
  paperStyle: { padding: '20px', borderRadius: '20px' },
  headingStyle: { fontWeight: 'bold' },
};

interface UsersRoleComponent {
  name: string;
  isLoading: boolean;
}

export const UsersRoleComponent = ({ name, isLoading }: UsersRoleComponent) => {
  const { setValue, getValues } = useFormContext();
  const [openDialog, setOpenDialog] = useState(false);
  const [userRoles, setUserRoles] = useState<any>();

  const handleOpenSelectServiceDialog = () => {
    setOpenDialog(true);
  };

  const combinedIDs = (data: any[]) => {
    const updatedData = data?.map((item: any) => ({
      ...item,
      id: `${item?.userID}_${item?.roleID}`
    }));
    return updatedData
  }

  useEffect(() => {
    if (getValues(name)) {
      setUserRoles(combinedIDs(getValues(name) ?? []))
    }
  }, [getValues(name)])

  const handleSelectService = (users: any[]) => {
    setOpenDialog(false);
    setUserRoles(combinedIDs(users ?? []));
    setValue(name, combinedIDs(users));
  };

  const handleCloseSelectServiceDialog = () => {
    setOpenDialog(false);
  };

  // -------------------------------------------------Grid Layout------------------------------

  const {
    isLoading: isLoadingGridLayout,
    data: gridLayouts,
    refetch: refetchGridLayout,
  } = useLoadData<ApiResponse<GridPreference>>(gridApiKeyStore.getGridLayout('Office', 'officeUsers'));

  const { mutateAsync } = useSaveData<any>(gridApiKeyStore.saveGridLayOut());
  const [gridConfig, setGridConfig] = useState<{
    dataState: State | undefined;
    columns: any[] | undefined;
  }>();

  const [filteredUsers, setFilteredUsers] = useState<any[]>([]);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    const filteredUserList = (userRoles || []).filter((user: any) =>
      Object.values(user).some(
        (value) => value && value.toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      )
    );
    setFilteredUsers(filteredUserList || []);
  }, [debouncedSearchTerm, userRoles]);

  const handleInputChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSaveLayout = (gridDataState: State, gridColumns: GridColumnConfig[]) => {
    const data = {
      filterJSON: JSON.stringify(gridDataState),
      columnJSON: JSON.stringify(gridColumns),
      gridName: 'officeUsers',
      pageName: 'Office',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  const handleResetLayout = () => {
    const data = {
      filterJSON: null,
      columnJSON: null,
      gridName: 'officeUsers',
      pageName: 'Office',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  useEffect(() => {
    if (!gridLayouts?.data) return;

    try {
      const filterData = gridLayouts.data.filterJSON ? JSON.parse(gridLayouts.data.filterJSON) : null;

      const columnJson = gridLayouts.data.columnJSON ? JSON.parse(gridLayouts.data.columnJSON) : null;

      //   if (filterData && columnJson) {
      setGridConfig({
        columns: columnJson,
        dataState: filterData,
      });
      //   }
    } catch (error) {
      console.log('Error parsing grid layout JSON:', error);
    }
  }, [gridLayouts?.data]);

  // ----------------------------------------------------------------------------------------

  return (
    <Card elevation={0}>
      <CardHeader
        className="subHead"
        title={
          <Typography variant="h3" color="textPrimary" style={styling.headingStyle}>
            USERS
          </Typography>
        }
        action={
          <Button className="btnLink" variant="text" sx={{ p: 0, m: 0, mr: 1 }} onClick={handleOpenSelectServiceDialog}>
            Add User
          </Button>
        }
      />
      <CardContent>
        <Grid container>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <SiTrackGrid
              fileTitle='office_user'
              dataItemKey="id"
              columns={roleGridColumns}
              //   dataItems={getValues(name)}
              actionPermissions={{
                export: UserPermissionName.DOWNLOAD_GRID,
              }}
              isBusy={isLoading}
              // -----------------Grid Layout-------
              dataItems={filteredUsers || []}
              reorderable
              // handleSearch={handleInputChange}
              handleSaveLayout={handleSaveLayout}
              isSaveLayout={true}
              gridConfigFromApi={gridConfig}
              handleResetLayout={handleResetLayout}
            />
          </Grid>
        </Grid>
        {openDialog && (
          <UserRoleDialog
            name={name}
            open={openDialog}
            onSelectedUsers={handleSelectService}
            onClose={handleCloseSelectServiceDialog}
          />
        )}
      </CardContent>
    </Card>
  );
};
